import { Tooltip } from '@iwoca/orion';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useNumberFormat } from 'hooks/useNumberFormat';
import styles from './FlexiLoanFeatures.module.css';

const useSharedFlexiLoanFeaturesTable = () => {
  const { splitTests, introducerCompany } = useIntroducerData();
  const isNewCommission = splitTests?.commission_aware_pricing_enabled;
  const isPrime = splitTests?.introducer_account_prime_customer_low_pricing;
  const { formatPercentage } = useNumberFormat();

  const thirtyDayInterestRates = {
    twelve: isPrime ? 'From 1.59%' : 'From 2.45%',
    fifteen: isPrime ? 'From 1.59%' : 'From 2%',
    twentyFour: isPrime ? 'From 1.08%' : 'From 1.95%',
    thirtySixToSixty: isPrime ? 'From 0.95%' : 'From 1.5%',
  };

  const withdrawalFees = {
    twelve: isNewCommission ? '0 - 5% set by you' : '0%',
    fifteen: '0%',
    twentyFour: isNewCommission ? '0 - 10% set by you' : '0 - 9% set by you',
    thirtySixToSixty: isNewCommission ? '1 - 10% set by you' : '6%',
  };

  const initialCommissions = {
    twelve: isNewCommission ? '5 - 10% *' : 'Up to 5%',
    fifteen: isNewCommission ? '5%' : 'Up to 5%',
    twentyFour: isNewCommission ? '0 - 10% *' : '100% of the withdrawal fee',
    thirtySixToSixty: isNewCommission ? '0 - 9% *' : '5% of first funding',
  };

  const repeatCommissions = {
    twelve: isNewCommission ? '0 - 2.5%' : 'Up to 5%',
    fifteen: isNewCommission ? '-' : 'Up to 5%',
    twentyFour: isNewCommission
      ? '0 - 5%'
      : '100% of the withdrawal fee for 24 months',
    thirtySixToSixty: isNewCommission ? '0 - 5%' : '-',
  };

  const interestOnlyRepayments = {
    twelve: '-',
    fifteen: 'First 3 months',
    twentyFour: '-',
    thirtySixToSixty: '-',
  };

  const features = [
    { key: 'twelve', label: '12 month' },
    { key: 'fifteen', label: '15 month' },
    { key: 'twentyFour', label: '24 month' },
    { key: 'thirtySixToSixty', label: '36-60 month' },
  ] as const;

  const data = features
    .filter(
      (feature) =>
        splitTests?.introducer_interest_only_loan_customer_rct_may24 ||
        feature.key !== 'fifteen',
    )
    .map((feature) => ({
      feature: feature.label,
      thirtyDayInterestRate: thirtyDayInterestRates[feature.key],
      withdrawalFee: withdrawalFees[feature.key],
      initialCommission: initialCommissions[feature.key],
      repeatCommission: repeatCommissions[feature.key],
      interestOnlyRepayments: interestOnlyRepayments[feature.key],
    }));

  return {
    data,
    preferredCommissionRateMessage:
      introducerCompany?.preferred_commission_rate && isNewCommission
        ? ` * Offers will aim to start at your preferred rate of commission where possible. Your current preferred rate is ${formatPercentage(
            introducerCompany?.preferred_commission_rate,
          )}. Please speak to iwoca if you would like to request a change.`
        : null,
    isNewCommission,
  };
};

export const useFlexiLoanFeaturesTablePDF = () => {
  const { data, preferredCommissionRateMessage } =
    useSharedFlexiLoanFeaturesTable();

  return {
    data,
    labels: {
      feature: 'Features',
      thirtyDayInterestRate: '30 day interest rate',
      withdrawalFee: 'Withdrawal fee',
      initialCommission:
        'Percentage applies to every funding in the first 30 days.',
      repeatCommission:
        'Percentage applies to every funding on days 31 to 365.',
      interestOnlyRepayments: 'Interest-only repayments',
    },
    preferredCommissionRateMessage,
  };
};

export const useFlexiLoanFeaturesTable = () => {
  const { data, isNewCommission, preferredCommissionRateMessage } =
    useSharedFlexiLoanFeaturesTable();

  return {
    data,
    labels: {
      feature: 'Features',
      thirtyDayInterestRate: '30 day interest rate',
      withdrawalFee: 'Withdrawal fee',
      initialCommission: (
        <div className={styles.tooltipRow}>
          Initial commission
          {isNewCommission && (
            <Tooltip content="Percentage applies to every funding in the first 30 days." />
          )}
        </div>
      ),
      repeatCommission: (
        <div className={styles.tooltipRow}>
          Repeat commission
          {isNewCommission && (
            <Tooltip content="Percentage applies to every funding on days 31 to 365." />
          )}
        </div>
      ),
      interestOnlyRepayments: 'Interest-only repayments',
    },
    preferredCommissionRateMessage,
  };
};
